import { HotelDetailViewModel, HotelViewModel } from '@api/index';
import { getHotelDetailChildAndVariationsInfo } from '@utils/utils';

import { BookerSearchViewModel } from '../../components/views/landing/components/booker/common/BookerSearchDrawer';

export const addHotelDetailGTM = (hotelDetail: HotelDetailViewModel) => {
  //GTM - Hotel Detail View

  const {
    variations: [variationWithMinPrice, variationWithMaxPrice],
    hasChildFreeStatus,
  } = getHotelDetailChildAndVariationsInfo(hotelDetail);
  const priceInterval =
    variationWithMinPrice && variationWithMaxPrice
      ? variationWithMinPrice?.variation?.totalSellingPrice +
        ' - ' +
        variationWithMaxPrice?.variation?.totalSellingPrice
      : '';
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product View',
    Label: 'Product View',
    noninteraction: true,
    ecommerce: {
      detail: {
        products: [
          {
            name: hotelDetail.name, // Name
            id: hotelDetail.id, // Product ID
            price: hotelDetail.lowestPrice?.price, // Product Price
            brand: 'Setur', // Product Brand (Contrat
            category: hotelDetail.categoryName, // Product Category
            variant: hotelDetail.boardType, // Product Variant
            dimension59: 'Otel', // Product Service Type
            dimension60: 'Erken Rezervasyon', // Product Campaign //TODO: {ilker.yildirim} should not be static
            dimension61: hotelDetail.mainFacilities
              ?.map((facility) => facility.name)
              .join(', '), // Product Theme
            dimension62: hotelDetail.lowestPrice?.discountRatio?.toString(), // Product Discount Rate
            dimension63: hotelDetail.images?.length.toString(), // Product Photo Count
            dimension65: priceInterval, // Product Price Interval
            dimension66: hotelDetail.lowestPrice?.originalPrice, // Product Real Price
            dimension68: 'YES', // Product Payment Campaign Status //TODO: {ilker.yildirim} should not be static
            dimension69: 'HOPI1500', // Product Payment Campaign Details //TODO: {ilker.yildirim} should not be static
            dimension70: hotelDetail.breadcrumbs
              ?.map((breadcrumb) => breadcrumb.title)
              .join(' > '), // Product Breadcrumb
            dimension71: hotelDetail.country, // Product Hotel Country
            dimension72: hotelDetail.city, // Product Hotel City
            dimension74:
              hotelDetail.regionTree?.find((region) => !region.parentRegionId)
                ?.regionName || '', // Product Hotel Region
            dimension76: variationWithMinPrice?.boardType, // Product Accomodation Type
            dimension77: undefined, // Product Hotel Event Status
            dimension78: hotelDetail.features
              ?.map((feature) => feature.name)
              .join(', '), // Product Hotel Features
            dimension80: hasChildFreeStatus, // Product Hotel Child Free Status
            dimension81: hotelDetail.chainId ? 'YES' : 'NO', // Product Chain Hotel Status
            dimension82: undefined, // Product Hotel Campaign Status
            dimension83: hotelDetail.promotions
              ?.map((promotion) => promotion.name)
              .join('|'), // Product Hotel Campaign Codes
            dimension84: hotelDetail.promotions?.length ? 'YES' : 'NO', // Product Hotel Available Room Status //TODO: {ilker.yildirim} fiyat varsa 'YES' değilse 'NO'
            dimension85: hotelDetail.rooms?.map((room) => room.name).join('|'), // Product Hotel Available Room Names
            metric1: hotelDetail.lowestPrice?.discountRatio, // Product Discount (Numerically)
          },
        ],
      },
    },
    event: 'eeEvent',
  });
};

export const addHotelSearchBarItemClickGTM = (item: BookerSearchViewModel) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product Impressions',
    Label: 'Product Impressions - Home Page',
    noninteraction: true,
    ecommerce: {
      currencyCode: 'TRY',
      impressions: [
        {
          name: item.text, // Name or ID is required.
          id: item.id, // Product ID
          list: 'Home Page - Search Bar List',
          position: 1,
          dimension59: 'Hotel',
        },
      ],
    },
    event: 'eeEvent',
  });
};

type HotelRoomEventType = {
  title: string;
  hotelName: string;
  checkInDate: string;
  checkOutDate: string;
  childCount?: number;
  adultCount?: number;
  price?: number;
  serviceType: string;
};

export const addHotelRoomSelectionClickEventToGTM = ({
  title,
  hotelName,
  checkInDate,
  checkOutDate,
  childCount,
  adultCount,
  serviceType,
  price,
}: HotelRoomEventType) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product Room Selection',
    Label: hotelName + '|' + title,
    cd_selected_room_price: price,
    cd_service_date: checkInDate + '|' + checkOutDate,
    cd_passangers_count: adultCount + '|' + childCount,
    cd_service_type: serviceType,
    noninteraction: false,
    event: 'gaEvent',
  });
};

export const addHotelLetUsCallYouClickEventToGTM = ({
  title,
  hotelName,
  checkInDate,
  checkOutDate,
  childCount,
  adultCount,
  serviceType,
}: HotelRoomEventType) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Biz Sizi Arayalım',
    Label: hotelName + '|' + title,
    cd_service_date: checkInDate + '|' + checkOutDate,
    cd_passangers_count: adultCount + '|' + childCount,
    cd_service_type: serviceType,
    noninteraction: false,
    event: 'gaEvent',
  });
};

export const addHotelLetUsCallYouSuccessEventToGTM = ({
  title,
  hotelName,
  checkInDate,
  checkOutDate,
}: HotelRoomEventType) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Biz Sizi Arayalım - Lead Success',
    Label: hotelName + '|' + title,
    cd_service_date: checkInDate + '|' + checkOutDate,
    noninteraction: false,
    event: 'gaEvent',
  });
};

type HotelListItemProps = {
  hotel: HotelViewModel;
  position: number;
  productListType: string;
  currency: string;
  checkInDate?: string;
  checkInWeekDay?: string;
  checkOutDate?: string;
  checkOutWeekDay?: string;
  listingFromPage: string;
};

const getHotelListItemEvent = ({
  hotel,
  position,
  productListType,
  checkInDate,
  checkInWeekDay,
  checkOutDate,
  checkOutWeekDay,
}: Omit<HotelListItemProps, 'currency' | 'listingFromPage'>) => ({
  name: hotel.name, // Name or ID is required. +
  id: hotel.id, // Product ID +
  price: hotel.pricing?.original, // Product Price +
  brand: 'Setur', // Product Brand (Contrat Status) +
  category: hotel.category, // Product Category +
  variant: hotel.boardType, // Product Variant +
  list: productListType, //'Listing Page - Antalya Otelleri List', // Product List Type
  position, // Product Position
  dimension59: 'Otel', // Product Service Type
  dimension60: 'Erken Rezervasyon', // Product Campaign - promotions
  dimension61: hotel.themes.map((theme) => theme.value).join(','), // Product Theme
  dimension62: hotel.pricing?.rate && hotel.pricing?.rate + '%', // Product Discount Rate
  dimension63: '', // Product Photo Count - tek bir image bulunmaktadır.
  dimension64: '', // Product Comment Count - no data available.
  dimension65: hotel.pricing?.discounted?.toString(), // Product Prive Interval - tek bir price bulunmaktadır
  dimension66: hotel.pricing?.original, // Product Real Price
  dimension67: hotel.installments ? 'YES' : 'NO', // Product Installment Status
  dimension68: hotel.promotions.length ? 'YES' : 'NO', // Product Payment Campaign Status
  dimension69: hotel.promotions.map((promotion) => promotion.name).join('|'), // Product Payment Campaign Details + birden fazla campaign status gelebilir
  // dimension70: 'Setur > Antalya > Kemer > NG Afyon', // Product Breadcrumb - adres verisini almamız gerekmektedir.
  // dimension71: 'Türkiye', // Product Hotel Country - adres verisini almamız gerekmektedir.
  // dimension72: 'Antalya', // Product Hotel City - adres verisini almamız gerekmektedir.
  // dimension73: 'Kemer', // Product Hotel Town - adres verisini almamız gerekmektedir.
  // dimension74: 'Merkez', // Product Hotel Region - adres verisini almamız gerekmektedir.
  // dimension75: '4.6', // Product Hotel Total Score - bulunmamaktadır.
  dimension76: hotel.boardType, // Product Accomodation Type + board type
  // dimension77: 'NO', // Product Hotel Event Status - no data available.
  dimension78: hotel.features
    .slice(0, 3)
    .map((feature) => feature.value)
    .join(','), // Product Hotel Features + maks ilk 3 tanesi göndeilecek
  // dimension79: 'NO', // Product Hotel Adult Free Status - no data available.
  // dimension80: 'YES', // Product Hotel Child Free Status - no data available.
  // dimension81: 'YES', // Product Chain Hotel Status - no data available.
  // dimension82: 'YES', // Product Hotel Campaign Status ? campaign status varsa ok (68 ile duplicated)
  // dimension83: 'HOPI1500|KOCAILEM', // Product Hotel Campaign Codes ? birden fazla campaign status gelebilir (69 ile duplicated)
  dimension84: hotel.pricing?.original ? 'YES' : 'NO', // Product Hotel Available Room Status + price varsa 'YES'
  // dimension85: 'Superior Oda|Suit Oda|Dubleks Aile Odası', // Product Hotel Available Room Names -
  dimension86: 'YES', // Product Hotel Available Room Status - 84 ile duplicated
  dimension109: checkInDate || 'Not Selected', // Check-in Date +
  dimension110: checkInWeekDay || 'Not Selected', // Check-in Day +
  dimension111: checkOutDate || 'Not Selected', // Check-out Date +
  dimension112: checkOutWeekDay || 'Not Selected', // Check-out Day +
  metric1: hotel.pricing?.rate, // Product Discount (Numerically)
});

export const addHotelListItemToGTM = ({
  currency,
  listingFromPage,
  ...otherParams
}: HotelListItemProps) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product Impressions',
    Label: 'Product Impressions - ' + listingFromPage,
    noninteraction: true,
    ecommerce: {
      currencyCode: currency,
      impressions: [getHotelListItemEvent(otherParams)],
    },
    event: 'eeEvent',
  });
};

export const addHotelListItemClickEventToGTM = (hotel: HotelListItemProps) => {
  dataLayer.push({
    Category: 'Enhanced Ecommerce',
    Action: 'Product Click',
    Label: 'Product Click - ' + hotel.listingFromPage,
    noninteraction: false,
    ecommerce: {
      click: {
        actionField: {
          list: hotel.listingFromPage + ' - ' + hotel.productListType, // Product List Type
        },
        products: [getHotelListItemEvent(hotel)],
      },
    },
    event: 'eeEvent',
  });
};
